import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GraduationCap, AlertCircle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";

const SP_PER_LEVEL = [0, 100, 250, 500, 1000, 2000];

const ALL_SKILLS = [
  'Management', 'Strategy', 'Commercial Negotiations', 'Development', 'AI',
  'Hardware Engineering', 'Legal', 'Accounting', 'Forecasting', 'Marketing',
  'Sales', 'Product', 'Design', 'Data Analysis', 'Operations', 'Other'
];

export default function TrainingRequestDialog({ open, onClose, currentSkills, officerEmail }) {
  const queryClient = useQueryClient();
  const [selectedSkill, setSelectedSkill] = useState('');
  const [targetLevel, setTargetLevel] = useState(2);
  const [justification, setJustification] = useState('');

  const createRequestMutation = useMutation({
    mutationFn: (data) => base44.entities.TrainingRequest.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingRequests'] });
      onClose();
      resetForm();
    }
  });

  const resetForm = () => {
    setSelectedSkill('');
    setTargetLevel(2);
    setJustification('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const skill = currentSkills.find(s => s.name === selectedSkill);
    const currentLevel = skill?.level || 0;
    const currentXp = skill?.xp || 0;
    const spNeeded = SP_PER_LEVEL[targetLevel] - currentXp;

    createRequestMutation.mutate({
      officer_email: officerEmail,
      skill_name: selectedSkill,
      current_level: currentLevel,
      target_level: targetLevel,
      sp_requested: spNeeded,
      justification,
      status: 'pending'
    });
  };

  const getSkillData = (skillName) => {
    const existing = currentSkills.find(s => s.name === skillName);
    return existing || { name: skillName, level: 0, xp: 0 };
  };

  const selectedSkillData = selectedSkill ? getSkillData(selectedSkill) : null;
  const maxTargetLevel = selectedSkillData ? Math.min(selectedSkillData.level + 2, 5) : 5;

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="skills-trainingrequestdialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-indigo-400" />
            Request Training
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Select Skill to Train</Label>
            <Select value={selectedSkill} onValueChange={setSelectedSkill}>
              <SelectTrigger className="bg-slate-950 border-slate-700">
                <SelectValue placeholder="Choose a skill..." />
              </SelectTrigger>
              <SelectContent>
                {ALL_SKILLS.map((skillName) => {
                  const skillData = getSkillData(skillName);
                  if (skillData.level >= 5) return null;
                  return (
                    <SelectItem key={skillName} value={skillName}>
                      {skillName} (Current: Lvl {skillData.level})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {selectedSkill && selectedSkillData && (
            <>
              <div className="p-3 bg-slate-950 rounded-lg border border-slate-800 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Current Level</span>
                  <Badge variant="outline" className="text-amber-400 border-amber-400/30">
                    Level {selectedSkillData.level}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Current SP</span>
                  <span className="text-indigo-400 font-mono">{selectedSkillData.xp || 0}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Target Level</Label>
                <Select 
                  value={targetLevel.toString()} 
                  onValueChange={(v) => setTargetLevel(parseInt(v))}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: maxTargetLevel - selectedSkillData.level }, (_, i) => {
                      const level = selectedSkillData.level + i + 1;
                      const spNeeded = SP_PER_LEVEL[level] - (selectedSkillData.xp || 0);
                      return (
                        <SelectItem key={level} value={level.toString()}>
                          Level {level} (Requires {spNeeded} SP)
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5" />
                  <div className="text-xs text-amber-400">
                    <p className="font-semibold mb-1">Training requires Commander approval</p>
                    <p className="text-amber-400/80">
                      SP Required: {SP_PER_LEVEL[targetLevel] - (selectedSkillData.xp || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label>Justification</Label>
            <Textarea data-testid="skills-trainingrequestdialog-textarea"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Explain why this training is important for your role..."
              className="bg-slate-950 border-slate-700 h-24"
              required
            />
          </div>

          <div className="flex gap-2">
            <Button data-testid="skills-cancel-btn" type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              disabled={!selectedSkill || !justification.trim()}
            >
              Submit Request
            </Button>
          </div>
        </form>
      </DialogContent>
    </ConfirmableDialog>
  );
}